<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import MapStyleSelector from "../../maps/MapStyleSelector.vue";
import MapZoomControls from "../../maps/MapZoomControls.vue";
import { getAccountMapStyle, resolveMapStyle } from "../../maps/map-style-utils";
import { createBultoLocationMapController } from "../../maps/bulto-location-map-controller";
import { accountStore } from "@/_store/accountStore";

const props = defineProps({
  histories: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: "",
  },
});

const accountsStore = accountStore();
const mapContainer = ref(null);
const mapStyle = ref(resolveMapStyle(""));
const displayMode = ref("current");
let hasCustomMapStyleSelection = false;
let controller = null;

function applyHistories() {
  if (!controller) return;
  controller.setHistories(props.histories);
}

onMounted(() => {
  mapStyle.value = getAccountMapStyle(accountsStore.account);
  if (!accountsStore.account) {
    accountsStore.getAccount().catch(() => null);
  }

  controller = createBultoLocationMapController({
    mapElement: mapContainer.value,
    initialSubscriptionKey: process.env.VUE_APP_AZURE_MAPS_SUBSCRIPTION_KEY || "",
    initialStyle: mapStyle.value,
  });
  applyHistories();
});

onBeforeUnmount(() => {
  controller?.destroy();
  controller = null;
});

watch(
  () => props.histories,
  () => applyHistories()
);

watch(
  () => accountsStore.account,
  (value) => {
    if (!value || hasCustomMapStyleSelection) return;
    const nextStyle = getAccountMapStyle(value);
    if (nextStyle === mapStyle.value) return;
    mapStyle.value = nextStyle;
    controller?.setStyle(nextStyle);
  }
);

function onStyleChange(style) {
  hasCustomMapStyleSelection = true;
  mapStyle.value = style;
  controller?.setStyle(style);
}

function onZoomIn() {
  controller?.zoomIn();
}

function onZoomOut() {
  controller?.zoomOut();
}

function onFit() {
  controller?.fitMap();
}

function toggleDisplayMode() {
  displayMode.value = displayMode.value === "trail" ? "current" : "trail";
  controller?.setDisplayMode(displayMode.value);
}
</script>

<template>
  <div class="bulto-location-map-stage">
    <div class="map-overlay-controls">
      <div class="map-control-group map-control-group--mode">
        <button
          v-tooltip="displayMode === 'trail' ? 'Ver solo última ubicación' : 'Ver historial completo'"
          type="button"
          class="map-control-btn traffic-btn"
          :class="{ 'is-active': displayMode === 'trail' }"
          :aria-label="displayMode === 'trail' ? 'Ver solo última ubicación' : 'Ver historial completo'"
          @click="toggleDisplayMode"
        >
          Historial
        </button>
      </div>
      <div class="map-control-group map-control-group--zoom">
        <map-style-selector :model-value="mapStyle" @change="onStyleChange" />
        <map-zoom-controls @zoom-in="onZoomIn" @zoom-out="onZoomOut" @fit="onFit" />
      </div>
    </div>

    <div id="bulto-location-map" ref="mapContainer" role="application" aria-label="Mapa de ubicacion del bulto"></div>

    <div v-if="props.isLoading" class="map-loading-overlay">
      <div class="spinner-border spinner-border-sm text-primary me-2" role="status" aria-hidden="true"></div>
    </div>

    <div v-if="props.errorMessage" class="map-error-overlay">
      {{ props.errorMessage }}
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "../../maps/map-overlay-controls";

.bulto-location-map-stage {
  background: #f8fafc;
  border-radius: 14px;
  height: 100%;
  min-height: 420px;
  overflow: hidden;
  position: relative;
  width: 100%;
}

#bulto-location-map {
  border-radius: 14px;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  width: 100%;
}

.map-control-group--mode {
  left: 14px;
  top: 14px;
}

.map-control-group--zoom {
  align-items: center;
  bottom: 14px;
  flex-direction: column;
  right: 14px;
}

:deep(.bulto-location-marker-history) {
  align-items: center;
  background: #8898aa;
  border: 2px solid #ffffff;
  border-radius: 50%;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.18);
  color: #ffffff;
  cursor: pointer;
  display: flex;
  font-size: 11px;
  height: 20px;
  justify-content: center;
  position: relative;
  transform: translate(-50%, -50%);
  width: 20px;
}

:deep(.bulto-location-marker-count) {
  align-items: center;
  background: #f5365c;
  border: 1px solid #ffffff;
  border-radius: 999px;
  color: #ffffff;
  display: flex;
  font-size: 9px;
  font-weight: 700;
  height: 15px;
  justify-content: center;
  line-height: 1;
  min-width: 15px;
  padding: 0 3px;
  position: absolute;
  right: -4px;
  top: -4px;
  z-index: 2;
}

:deep(.bulto-location-marker-current) {
  align-items: center;
  cursor: pointer;
  display: flex;
  height: 34px;
  justify-content: center;
  position: relative;
  transform: translate(-50%, -50%);
  width: 34px;
}

:deep(.bulto-location-marker-pulse) {
  animation: bulto-location-pulse 1.8s ease-out infinite;
  background: transparent;
  border-radius: 50%;
  box-shadow:
    0 0 0 2px rgba(255, 255, 255, 0.95),
    0 0 0 4px rgba(94, 114, 228, 0.85);
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
}

:deep(.bulto-location-marker-core) {
  align-items: center;
  background: #5e72e4;
  border: 2px solid #ffffff;
  border-radius: 50%;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.18);
  color: #ffffff;
  display: flex;
  font-size: 13px;
  height: 22px;
  justify-content: center;
  position: relative;
  width: 22px;
  z-index: 1;
}

@keyframes bulto-location-pulse {
  0% {
    opacity: 0.55;
    transform: scale(0.6);
  }

  100% {
    opacity: 0;
    transform: scale(1.6);
  }
}
</style>
